import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as LogRocket from 'logrocket';
import { combineLatest, EMPTY, interval, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  finalize,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { AnalyticsService } from '@libs/src/analytics/analytics.service';
import { IntercomService } from '@libs/src/intercom/intercom.service';
import { CrossTabListenerService } from '@main-client/src/app/core/cross-tab-listener.service';
import { TenantLocalesBackend } from '@main-client/src/app/tenant-locale/tenant-locales.backend';
import { TranslationLocalStorageService } from '@main-client/src/app/translation-local-storage/translation-local-storage.service';
import { UserIdleTimeoutService } from '@main-client/src/app/user-idle-timeout/user-idle-timeout.service';

import { environment } from '@libs/src/environments/environment';
import { UserContentTranslation } from '@libs/src/locale-lookup/locale-translations-map.interface';
import { Account } from '@libs/src/models/account.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { getCurrentSubdomain } from '@libs/src/utilities/url.utilities';
import { TenantLocale } from '@main-client/src/app/tenant-locale/tenantlocale.model';
import {
  isAnyAdmin,
  isGroupAdmin,
  isTenantAdmin,
} from '@main-client/src/app/user/user.helpers';

import { IAppState } from '../app.state';

import { RouteListenerService } from '../core/route-listener.service';
import { UpdatesService } from '../core/updates.service';
import { TenantService } from '../tenant/tenant.service';
import { UserService } from '../user/user.service';

import defer from 'lodash-es/defer';
import get from 'lodash-es/get';
import includes from 'lodash-es/includes';
import isEmpty from 'lodash-es/isEmpty';

const logRocketAppId = environment.logRocket.app_id;
const POLLING_INTERVAL = 100;

@Component({
  selector: 'app-root',
  templateUrl: './root.component.html',
})
export class RootComponent implements OnInit, OnDestroy {
  header = this.store.select('header');
  tenant = this.store.select('tenant');
  user = this.store.select('user');
  componentDestroy = new Subject<void>();
  isEmptyState = false;
  routeTitle: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private crossTabListenerService: CrossTabListenerService,
    private routeListenerService: RouteListenerService,
    private router: Router,
    private store: Store<IAppState>,
    private tenantService: TenantService,
    private tenantLocaleService: TenantLocalesBackend,
    private translationStorageService: TranslationLocalStorageService,
    private updatesService: UpdatesService,
    private userService: UserService,
    public intercomService: IntercomService,
    public userIdleTimeoutService: UserIdleTimeoutService,
  ) {}

  ngOnInit(): void {
    this.routeListenerService.init();
    this.analyticsService.initialize();
    this.tenantService.populate();
    this.crossTabListenerService.addListeners();
    this.intercomService.boot();
    this.startAuthWatcher()
      .pipe(
        finalize(() => {
          this.userService.populate();

          defer(() => {
            this.updatesService.init();
          });
        }),
      )
      .subscribe();
    const userState = this.user.pipe(
      filter((user: Account) => !isEmpty(user)),
      take(1),
    );
    const tenantState = this.tenant.pipe(
      filter((tenant: Tenant) => !isEmpty(tenant)),
      take(1),
    );
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        window.scroll(0, 0);
        this.isEmptyState = this.routeListenerService.findDataValue(
          this.activatedRoute.snapshot,
          'isEmptyState',
        ) as boolean;
        this.routeTitle = this.getRouteTitle(
          this.router.routerState.root.firstChild,
        );
      });

    combineLatest([userState, tenantState])
      .pipe(take(1))
      .subscribe(([user, tenant]) => {
        this.initAdminIdleTimeout(user);
        if (
          logRocketAppId &&
          (includes(['academic', 'organization'], tenant.partner_type) ||
            includes(['dream'], tenant.name))
        ) {
          LogRocket.init(logRocketAppId, {
            ingestServer: '/__logger',
            sdkServer: '/__logger-cdn',
          });
          LogRocket.identify(user._id, { tenant_name: tenant.name });
        }
        this.tenantLocaleService
          .getTenantLocales()
          .pipe(
            catchError(() => EMPTY),
            tap((result: { tenantLocales: TenantLocale[] }) => {
              this.translationStorageService.setTranslationsMap(
                result.tenantLocales as UserContentTranslation[],
              );
            }),
          )
          .subscribe();
      });
    combineLatest([userState, tenantState])
      .pipe(
        take(1),
        concatMap(([user, tenant]) => {
          this.intercomService.toggleLauncher(true);
          return this.intercomService.updateUserProperties({
            email: user.email,
            groups: (user.groups || [])
              .map(({ document }) => document?.slug)
              .join(','),
            isAdmin: isAnyAdmin(user),
            isHubAdmin: isGroupAdmin(user),
            isTenantAdmin: isTenantAdmin(user),
            name: user.full_name,
            tenantName: getCurrentSubdomain(),
            tenantPartnerType: tenant.partner_type,
            user_id: user._id,
          });
        }),
        delay(2000),
        tap(() => this.intercomService.shutdown()),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.componentDestroy.next();
    this.componentDestroy.complete();
  }

  initAdminIdleTimeout(user: Account) {
    if (!isAnyAdmin(user)) {
      return;
    }
    return this.tenant
      .pipe(
        takeUntil(this.componentDestroy),
        filter((tenant: Tenant) => !isEmpty(tenant)),
        tap((tenant: Tenant) => {
          if (get(tenant, 'enabled_features.admin_lockout', false)) {
            return this.userIdleTimeoutService.initIdleTimeout();
          }
          this.userIdleTimeoutService.destroyIdleWatcher();
        }),
      )
      .subscribe();
  }

  getRouteTitle(currentRoute: ActivatedRoute): string {
    let title: string;

    while (currentRoute) {
      if (currentRoute.snapshot.routeConfig?.title) {
        title = currentRoute.snapshot.routeConfig.title as string;
      }

      currentRoute = currentRoute.firstChild;
    }

    return title;
  }

  startAuthWatcher() {
    return interval(POLLING_INTERVAL).pipe(
      takeWhile(() => !this.userService.isLoggedIn()),
    );
  }
}
